/**
 * Sister Lydia, the on-brand chat assistant.
 *
 * Persona: a gentle, faith-rooted companion with NASM-style coaching cues
 * (form, tempo, RIR, progressive overload) and a registered-dietitian lens
 * (kcal, protein, fiber, hydration, micronutrients). Named after Lydia of
 * Acts 16:14, "a seller of purple, a worshiper of God." She is patient,
 * practical, and ends with a verse or sister-affirmation when natural.
 *
 * Why local rules instead of an LLM call: this gives instant, deterministic
 * answers for the demo and never hits credits. The shape (intent ->
 * response) is identical to what an LLM router would produce, so we can
 * swap in `complete(persona, userMessage)` later without changing call sites.
 */

const PERSONA = {
  id: "u_lydia",
  name: "Sister Lydia",
  initials: "SL",
  threadId: "lydia",
  tagline: "Faith-rooted form & nourishment companion",
};

export const LYDIA = PERSONA;

type Intent =
  | "form-squat"
  | "form-deadlift"
  | "form-pushup"
  | "form-plank"
  | "soreness"
  | "rest-days"
  | "protein"
  | "kcal-deficit"
  | "fiber"
  | "hydration"
  | "postpartum"
  | "perimenopause"
  | "fasting-faith"
  | "weight-stall"
  | "motivation"
  | "verse-strength"
  | "fallback";

const RESPONSES: Record<Intent, string[]> = {
  "form-squat": [
    "Strong squats start at the foot, sister. Three points of contact: big toe, pinky toe, heel. Knees track over the middle three toes, ribs stacked over hips. Lower until thighs are parallel or just below if your hips allow. Drive the floor away on the way up. Two to three sets of eight, leaving two reps in the tank.",
  ],
  "form-deadlift": [
    "Bar over midfoot, hips higher than knees, lats packed like you're tucking oranges in your armpits. Take a breath into your belly, brace, then push the floor away while pulling the bar up your shins. Lock out by squeezing glutes, not by leaning back. If your low back tells you a story, the bar drifted forward.",
  ],
  "form-pushup": [
    "Hands a touch wider than shoulders, fingers spread, elbows tracking back at about a 45-degree angle, never flared straight out. Body in one line from crown to heel. Lower under control, three counts down, one count up. If full pushups still feel too steep, do them on a bench or counter, the angle is the regression, not the shame.",
  ],
  "form-plank": [
    "Stack shoulders over elbows, glutes squeezed, ribs pulled toward hips so the lower back doesn't sag. Breathe slow, in through the nose. Quality over time. Thirty honest seconds beats two minutes of a hammock spine.",
  ],
  soreness: [
    "Soreness is your tissue remodeling, not damage, as long as it eases by 48 to 72 hours. Walk gently, hydrate, get protein in (around 25 grams per meal), and warm shower or epsom soak. If pain is sharp, one-sided, or in a joint, that is a stop-and-message-Coach signal.",
  ],
  "rest-days": [
    "Rest is where the work takes root. For Rooted-tier rhythm I love three lifts, one walk-and-pray day, one yoga or mobility day, two true rests. The body is a garden, not a factory. Psalm 23:2, He makes me lie down in green pastures.",
  ],
  protein: [
    "A simple target: roughly 0.7 to 1.0 grams of protein per pound of goal bodyweight, split across three or four meals. Eggs, Greek yogurt, cottage cheese, chicken thigh, salmon, lentils with rice. If breakfast is hard, a 30-gram shake counts as ministry to your muscles.",
  ],
  "kcal-deficit": [
    "Gentle deficits beat aggressive ones, especially around cycle and stress. Aim for around 250 to 400 kcal under maintenance, protein and fiber held high, lift twice a week minimum so you keep the muscle you have. Track for 10 days, then assess. No fasting your whole life, sister.",
  ],
  fiber: [
    "Most women I walk with eat 12 to 15 grams of fiber a day; the body asks for 28 to 35. Add berries to breakfast, lentils or beans to one meal, a leafy salad at dinner, chia in your yogurt. Hydrate up as you do, or your gut will protest.",
  ],
  hydration: [
    "Half your bodyweight in ounces is a fine starting line. More on lifting days, more in heat, more around your cycle. A pinch of mineral salt or lemon helps absorption. If your urine looks like pale straw, you're tending well.",
  ],
  postpartum: [
    "First, breath. Diaphragm and pelvic floor work together: inhale belly expands, exhale ribs draw down and floor lifts. Walks, deep core work, and gentle goblet squats for the first 8 to 12 weeks before loaded barbell work. If you feel doming or leaking, slow down and tell Coach Kiley. You are tending a sanctuary.",
  ],
  perimenopause: [
    "This season asks for more protein (closer to 1.0g per pound of goal weight), more lifting (heavier, lower reps), more sleep, and gentler cardio. Magnesium glycinate at dusk, walk after dinner, earlier dinners. Strength training is the closest thing we have to medicine in this window.",
  ],
  "fasting-faith": [
    "Fasting can be beautiful when it's worship, not weight loss. Keep it short, keep it prayerful, break with protein and produce. If you are training for strength or in a cycle low-energy phase, eat. Your body's fuel is not a betrayal of devotion.",
  ],
  "weight-stall": [
    "Stalls are normal, sister. Check three things: are you sleeping seven plus hours, are protein and fiber where they should be, and is the scale really stalled or is it a 10-day flat patch? Weekly average matters more than any one morning. Sometimes the body needs a maintenance week before it lets go again.",
  ],
  motivation: [
    "Discipline is just remembered love. You loved your future self enough to plan this, and showing up is the prayer. Do the smallest version today, ten minutes is a holy yes. Galatians 6:9, let us not grow weary in doing good.",
  ],
  "verse-strength": [
    "Try Isaiah 40:31, those who hope in the Lord will renew their strength. Or Philippians 4:13. Or my favorite for hard sets, Nehemiah 8:10, the joy of the Lord is your strength.",
  ],
  fallback: [
    "I want to walk this through with you, sister. Tell me a little more: what does your week look like, where in your body are you feeling it, and what tier are you in? If this is something Coach Kiley should weigh in on directly, I can flag her for you.",
  ],
};

interface IntentRule {
  intent: Intent;
  any: RegExp[];
}

const RULES: IntentRule[] = [
  { intent: "form-squat", any: [/\bsquat/i, /\blunge/i] },
  { intent: "form-deadlift", any: [/\bdeadlift/i, /\brdl\b/i, /hip hinge/i] },
  { intent: "form-pushup", any: [/push.?up/i] },
  { intent: "form-plank", any: [/\bplank/i] },
  { intent: "soreness", any: [/\bsore/i, /\bdoms\b/i, /\bache/i] },
  { intent: "rest-days", any: [/rest day/i, /how many days/i, /how often.*lift/i, /split/i] },
  { intent: "protein", any: [/\bprotein/i] },
  { intent: "kcal-deficit", any: [/\bdeficit/i, /lose weight/i, /losing weight/i, /\bcut\b/i, /kcal/i, /calorie/i] },
  { intent: "fiber", any: [/\bfiber/i, /\bgut\b/i, /digest/i] },
  { intent: "hydration", any: [/\bwater\b/i, /hydrat/i] },
  { intent: "postpartum", any: [/postpartum/i, /after baby/i, /pelvic floor/i, /diasta/i] },
  { intent: "perimenopause", any: [/perimenopaus/i, /menopaus/i, /hot flash/i] },
  { intent: "fasting-faith", any: [/\bfast(ing)?\b/i] },
  { intent: "weight-stall", any: [/stall/i, /plateau/i, /scale.*stuck/i, /not losing/i] },
  { intent: "motivation", any: [/tired/i, /unmotivat/i, /quit/i, /give up/i, /can'?t do this/i] },
  { intent: "verse-strength", any: [/verse/i, /scripture/i, /bible/i] },
];

export function classifyIntent(text: string): Intent {
  const lower = text.toLowerCase();
  for (const rule of RULES) {
    if (rule.any.some((rx) => rx.test(lower))) return rule.intent;
  }
  return "fallback";
}

export function lydiaReply(text: string): string {
  const intent = classifyIntent(text);
  const pool = RESPONSES[intent];
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Soft greeting message Lydia opens every new sister thread with. */
export const LYDIA_OPENER =
  "Hey sister, I'm Lydia. I help with form, training rhythm, and gentle nourishment, all rooted in faith. Ask me anything, even small things. If you need Coach Kiley directly, your tier and our queue will guide that.";
